import { type FC, type FormEvent, type ReactElement, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useQueryClient } from 'react-query'
import { useAuth, useSessionFormPersist } from '@lib/hooks'
import {
  getTransactionFeeToolTipText,
  PaymentDetails,
  type PaymentFormData,
  PaymentType,
  type ScheduleResponse,
  useCheckoutPlan,
  useSchedulePayment,
  Variant, MTDtracking, getTipAmount,
  getTotalFees,
  ScrollHash, ActiveCampaign,
  ApplicationName,
  MessageBox,
  getCampaignDays,
  CampaignSetup,
  AdditionalContribution,
  calculateDailyAmounts,
  scrollToHash,
  hasQurbaniCause,
  isQurbaniCauseSelected,
  totalQurbaniDonation,
  getCausesTotal,
  getEvergiveContribution,
  type Cause,
  QurbaniCategory,
  ERROR_MESSAGES,
  useResponsive,
  CheckoutEmail,
  ProductName,
  GiftAid,
  EverGiveConfirmation,
  PaymentErrorModal,
  Disclaimer
} from '@shamaazi/mytennights'
import { displayCurrency, fixTipValue, RecaptchaV3, Tip, tipValues } from '@lib/services'
import { Button, Modal, Validate, TransactionFee } from '@lib/components'
import giftAidLogo from '~/assets/giftaid.svg'
import { Step } from '~/components/CheckoutProgress'
import edit from '~/assets/icons/edit-secondary-5.svg'
import evergive from '~/assets/evergive.svg'

import { MytennightsTestId } from '@lib/testing'
import { PageForCheckoutFlow } from '~/components/Page'
import { Widget } from '~/components/Widget'
import { TipTooltipContent } from '~/components/TipToolTipContent'
import { TipSelection } from '~/components/TipSelection'

const MTDPaymentPage = 'payment'
const recaptchaV3 = new RecaptchaV3(MTDPaymentPage, ApplicationName.mytendays)

export const Payment: FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { isMobile } = useResponsive()
  const { plan, setPlan, resetPlan } = useCheckoutPlan()
  const [editTip, setEditTip] = useState(false)
  const [emailError, setEmailError] = useState<null | { message: string, colour: string, borderColour: string, fontColour: string }>(null)
  const [showPaymentErrorModal, setShowPaymentErrorModal] = useState(false)

  // Ensure that transaction fee is paid if Qurbani selected
  useEffect(() => {
    if (isQurbaniCauseSelected(plan)) {
      setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, payFees: true } })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const form = useForm<PaymentFormData>({
    defaultValues: {
      campaign: ActiveCampaign.myTenDays,
      addressLine1: '',
      country: plan.charity.country,
      email: user?.email ?? plan.donorDetails.email,
      firstName: '',
      giftAid: false,
      taxReceipt: false,
      lastName: '',
      postalCode: '',
      tipPercent: Tip.TenPercent,
      emailOptIn: false,
      charityEmailOptIn: false,
      paymentType: PaymentType.googlePay
    }
  })

  const tipAmount = getTipAmount(plan.causes, plan.paymentDetails.tipPercent, fixTipValue(plan.paymentDetails.tipValue))
  const totalFees = getTotalFees(plan, tipAmount)
  const qurbaniTotal = totalQurbaniDonation(plan)

  useEffect(() => {
    if (plan?.paymentDetails?.tipValue !== tipAmount) {
      setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, tipValue: tipAmount } })
    }
  }, [tipAmount, plan, setPlan])

  const onSuccessfulPayment = (scheduleResponse: ScheduleResponse, formData: PaymentFormData): void => {
    MTDtracking.paymentSuccessful(formData.email, Boolean(user), plan, scheduleResponse, tipAmount, totalFees, formData)
    queryClient.invalidateQueries('schedules').catch(() => { })

    const totalCauseDonation = Object.entries(plan.causes).reduce((sum, [_, amount]) => {
      return sum + amount
    }, 0)

    // setSharePageDetails is not saving the share details into the session storage at the moment
    // this needs to be looked into, but this fixes the problem for now.
    window.sessionStorage.setItem('share_page_details', JSON.stringify({
      email: formData.email,
      causes: plan.causes,
      tip: plan.paymentDetails.tipValue / 100,
      fees: totalFees,
      qurbani: qurbaniTotal,
      charity: plan.charity,
      split: plan.split,
      fridayGiving: plan.fridayGiving,
      fridayGivingOptIn: plan.fridayGivingOptIn,
      shareCode: scheduleResponse.shareCode,
      total: totalCauseDonation + tipAmount + totalFees,
      totalCauseDonation
    }))
    navigate('/share')
    resetPlan()
    resetForm()
  }

  const { submit, isLoading, error: paymentError, validationError } = useSchedulePayment(onSuccessfulPayment)

  const resetForm = useSessionFormPersist('checkout_payment', form.watch, form.setValue)
  const causeTotal = getCausesTotal(plan)
  const { arafahAmount, dailyAmount } = calculateDailyAmounts(plan.split, plan.multiplier, plan.startDateOffset, causeTotal - qurbaniTotal)
  const total = causeTotal + tipAmount + totalFees
  const openMTDCampaignDays = getCampaignDays(CampaignSetup.defaultDhulHijjahStartDate, plan.startDateOffset).filter(d => d.isOpen).length

  const onToggleFees = (): void => {
    setPlan({ ...plan, paymentDetails: { ...plan.paymentDetails, payFees: !plan.paymentDetails.payFees } })
  }

  const openEditTipModal = (): ReactElement => {
    return (<Modal overlay={isMobile} isOpen={editTip} onClose={() => setEditTip(false)} backgroundColor='bg-fg-gray-900' className='w-full md:w-medium-modal'>
      <div className='flex flex-col justify-between h-full gap-y-10 overflow-auto'>
        <Widget>
          <div className='flex flex-row gap-x-5 justify-between w-full md:mb-0 relative'>
            <h1 className="text-mtd-h4 font-bold text-mtd-secondary-5">Edit Your Tip</h1>
            <MessageBox variant={Variant.mtd} width={6} infoIcon tooltipBorder customBoxStyle="w-tooltip-box message-box">
              <TipTooltipContent charityID={plan.charity.charity_id} />
            </MessageBox>
          </div>
          <p className=" text-mtd-secondary-5 text-mtd-body-small font-normal text-opacity-60 mt-2">
            Your tip fuels our mission
          </p>
          <div className='mt-4'>
            <TipSelection />
          </div>
        </Widget>
        <div className='flex flex-col gap-y-3'>
          <div className="w-full flex flex-col items-center font-semibold mb-7 px-4">
            {plan.paymentDetails.tipPercent !== Tip.Other ? <div className="flex flex-col gap-y-2 w-full items-center mb-4">
              <span className="text-mtd-info text-mtd-secondary-3 font-normal">How tip is calculated</span>
              <div className="w-full flex items-center justify-between h-3">
                <span className="font-normal text-mtd-label">Causes</span>
                <span className="font-normal text-mtd-label"><span className="text-mtd-info text-mtd-secondary-3">{openMTDCampaignDays} x </span>{displayCurrency({ amount: dailyAmount * tipValues[plan.paymentDetails.tipPercent]! / 100, currency: plan.charity.currency })}</span>
              </div>
              {plan.split === 'more-arafah' && (
                <div className="w-full flex items-center justify-between text-mtd-info font-normal h-3">
                  <span className="text-mtd-label">{`On Arafah (Daily x${plan.multiplier})`}</span>
                  <span className="text-mtd-label">{displayCurrency({ amount: arafahAmount, currency: plan.charity.currency })}</span>
                </div>
              )}
              {qurbaniTotal ? (
                <div className="w-full flex items-center justify-between h-3">
                  <span className="font-normal text-mtd-label">Qurbani Total</span>
                  <span className="font-normal text-mtd-label">{displayCurrency({ amount: qurbaniTotal * tipValues[plan.paymentDetails.tipPercent]! / 100, currency: plan.charity.currency })}</span>
                </div>
              ) : null}
            </div> : null
            }
            <div className="w-full flex items-center justify-between text-mtd-h3 h-3.5">
              <span className="font-bold">Total tip {plan.paymentDetails.tipPercent === Tip.Other ? '' : `(${tipValues[plan.paymentDetails.tipPercent]}%)`}</span>
              <span className="font-bold text-mtd-h4">{displayCurrency({ amount: tipAmount, currency: plan.charity.currency })}</span>
            </div>
          </div>
          <div className='flex flex-col md:flex-row gap-4 mb-2'>
            <Button data-test-id={MytennightsTestId.updateTipButton} className="w-full flex flex-row items-center justify-center gap-x-2 font-bold" variant='mtd-primary' onClick={() => {
              setEditTip(false)
            }}>
              <div>Update</div>
            </Button>
            <Button className="w-full flex flex-row items-center justify-center gap-x-2 font-bold" variant='mtd-secondary-outline' onClick={() => {
              setEditTip(false)
            }}>
              <div>Cancel</div>
            </Button>
          </div>
        </div>
      </div>
    </Modal >)
  }
  useEffect(() => {
    if (paymentError && paymentError instanceof Error) {
      scrollToHash(ScrollHash.cardDetail)
      // do not show payment error modal for payment validation error
      if (paymentError.message === ERROR_MESSAGES.paymentDetailValidation) {
        return
      }
      setShowPaymentErrorModal(true)
    }
  }, [paymentError])

  const onPaymentTypeChange = (type: PaymentType): void => {
    form.setValue('paymentType', type)
    MTDtracking?.changePaymentType(user?.email, plan, type)
  }

  const validEmail = (): boolean => {
    if (!plan.donorDetails.email) {
      setEmailError({ message: 'Please enter an email to complete your donation.', colour: 'mtn-red-100', borderColour: 'mtn-red-100', fontColour: 'mtn-red-100' })
      scrollToHash(ScrollHash.email)
      return false
    } else if (Validate.validateEmail(plan.donorDetails.email) !== true) {
      setEmailError({ message: 'Please enter a valid email.', colour: 'mtn-red-100', borderColour: 'mtn-red-100', fontColour: 'mtn-red-100' })
      scrollToHash(ScrollHash.email)
      return false
    }
    setEmailError(null)
    MTDtracking.checkOutStarted(user?.email ?? plan.donorDetails.email, plan)
    return true
  }

  const submitForm = async (event?: FormEvent<HTMLFormElement>): Promise<void> => {
    // Prevent page reload
    event?.preventDefault()
    if (validEmail()) {
      const token = await recaptchaV3.getToken()
      form.setValue('recaptchaToken', token)
      form.setValue('recaptchaPage', MTDPaymentPage)
      await form.handleSubmit(data => submit(data))()
    }
  }

  const renderCheckoutSummary = (): ReactElement => {
    const qurbaniIds = plan.charity.causes.filter((cause: Cause) => cause.category === QurbaniCategory).map((cause: Cause) => cause.cause_id)

    return <section className="w-full border border-solid border-mtn-gray-250 bg-white p-4 pt-8 gap-y-4 flex flex-col items-center rounded-xl text-fg-blue-800">

      <div className="flex flex-col gap-y-4 px-2">
        {/* TOTAL SECTION */}
        <div className="flex flex-col items-center pt-3 pb-2 text-mtd-secondary-5">
          <h1 className="font-bold text-mtd-h1">{displayCurrency({ amount: total, currency: plan.charity.currency })}</h1>
          <span className="font-medium text-mtd-label">{`${openMTDCampaignDays} Day Total Donation`}</span>
          <span className="font-semibold text-mtd-label">to {plan.charity.charity_name}</span>
        </div>

        {/* CAUSE AMOUNT SECTION */}
        <div className="w-full flex flex-col items-center gap-y-3">
          {Object.entries(plan.causes).map(([causeId, causeAmount]) => (
            causeAmount && !qurbaniIds.includes(causeId) ? (
              <div key={causeId} className="w-full flex items-center justify-between text-mtd-lable">
                <span className="font-normal">{plan.charity.causes.find(c => c.cause_id === causeId)?.title}</span>
                <span className="font-semibold">{displayCurrency({ amount: causeAmount, currency: plan.charity.currency })}</span>
              </div>
            ) : null
          ))}
        </div>

        <hr className="w-full border border-b-0 border-solid border-mtd-gray-3" />

        {/* CAUSE TOTAL SECTION */}
        <div className="w-full flex flex-col items-center font-semibold">
          <div className="w-full flex items-center justify-between text-mtd-lable">
            <span className="font-bold">Cause Daily Total</span>
            <span className="font-bold">{displayCurrency({ amount: dailyAmount, currency: plan.charity.currency })}</span>
          </div>
          {plan.split === 'more-arafah' && (
            <div className="w-full flex items-center justify-between text-mtd-info font-normal">
              <span>{`On Arafah (Daily x${plan.multiplier})`}</span>
              <span>{displayCurrency({ amount: arafahAmount, currency: plan.charity.currency })}</span>
            </div>
          )}
        </div>

        <hr className="w-full border border-b-0 border-solid border-mtd-gray-3" />

        {/* QURBANI TOTAL SECTION */}
        {qurbaniTotal ? <>
          <div className="w-full flex flex-col items-center font-semibold">
            <div className="w-full flex items-center justify-between text-mtd-lable">
              <span className="font-bold">Qurbani Total</span>
              <span className="font-bold">{displayCurrency({ amount: qurbaniTotal, currency: plan.charity.currency })}</span>
            </div>
          </div>
          <hr className="w-full border border-b-0 border-solid border-mtd-gray-3" />
        </>
          : null}

        {/* FEE AND TIP SECTION */}
        <div className="flex flex-col gap-y-4">

          {/* FEE SECTION */}
          <TransactionFee
            currency={plan.charity.currency}
            payFees={plan.paymentDetails.payFees}
            onToggleFees={onToggleFees}
            toolTipText={getTransactionFeeToolTipText()}
            feeAmount={totalFees}
            allowToggle={!isQurbaniCauseSelected(plan)}
            DisableToggleMessage={() => <p>When you make a Qurbani donation, the transaction fee is mandatory to ensure the full amount is allocated to fulfil your religious obligation. Visit our <a href="https://about.shamaazi.com/mytendays/terms-and-conditions" target="_blank" rel="noreferrer" className="underline font-bold">T&amp;Cs</a> and <a href="https://about.shamaazi.com/mytendays/faq" target="_blank" rel="noreferrer" className="underline font-bold">FAQs</a> for more information.</p>}
          />

          {/* TIP SECTION */}
          <div className="flex flex-col">
            <div className={`flex items-center justify-between ${plan.paymentDetails.payTip ? 'text-mtd-secondary-5' : 'text-mtd-gray-6'}`}>
              <div className="flex gap-x-2 relative items-center">
                <div className='bg-mtd-gray-1 rounded-full p-1 h-6 w-6' onClick={() => setEditTip(!editTip)} data-test-id={MytennightsTestId.editTipButton}>
                  <img src={edit} alt='edit' className='' />
                </div>
                <span className="font-semibold text-mtd-label">Tip
                  {tipValues[plan.paymentDetails.tipPercent] ? ` (${tipValues[plan.paymentDetails.tipPercent]}%)` : ''}
                </span>
              </div>
              <span className={`${plan.paymentDetails.payTip ? '' : 'line-through text-mtd-gray-5'}`}>{displayCurrency({ amount: tipAmount, currency: plan.charity.currency })}</span>
            </div>
            <p className="ml-9 text-mtd-info font-normal">Your tip fuels our mission.</p>
          </div>
          {openEditTipModal()}
        </div>

      </div>
      {/* EVERGIVE SECTION */}
      {plan.evergiveSharePercentage
        ? <EverGiveConfirmation
      variant={Variant.mtd}
      image={<img className="w-4 h-4" src={evergive} alt="MyTenDays x EverGive" />}
      egAmount={displayCurrency({ amount: getEvergiveContribution(plan, plan.evergiveSharePercentage), currency: plan.charity.currency })}
      egPercentage={plan.evergiveSharePercentage} />
        : null}
    </section>
  }

  return <PageForCheckoutFlow progressBarStep={Step.Checkout} withQurbani={hasQurbaniCause(plan)} skinny >
    {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
    <form className="mx-auto font-medium gap-y-4 flex flex-col" onSubmit={submitForm}>
      <h1 className="mx-auto text-center py-2 text-xl font-bold text-fg-blue-310">Review & Pay</h1>
      {renderCheckoutSummary()}
      <CheckoutEmail form={form} variant={Variant.mtd} product={ProductName.myTenDays} emailError={emailError} validate={validEmail} planStore={{ plan, setPlan }} />
      <GiftAid
        form={form}
        image={<img alt="giftAid" src={giftAidLogo} width={114} height={40} className="object-cover" />}
        planStore={{ plan, setPlan }}
      />
      <PaymentDetails form={form} variant={Variant.mtd} tracking={MTDtracking} paymentLoading={isLoading} submitForm={submitForm} validationError={validationError} total={total} onPaymentTypeChange={onPaymentTypeChange} validate={validEmail} title={'Card Details'} />
      {plan.charity && plan.fridayGivingOptIn === 'yes' &&
        <AdditionalContribution isFG={true} plan={plan} />
      }
    <Disclaimer
              product={ProductName.myTenNights}
              planStore={{ plan }}
              termsLink={
                <Link to="/terms-and-conditions" className="font-bold underline">
                  terms
                </Link>
              }
              privacyLink={
                <Link to="/privacy-policy" className="font-bold underline">
                  privacy policy
                </Link>
              }
              charityPrivacyLink={
                <Link to={plan.charity.privacy_policy_url} target="_blank" rel="noreferrer noopener" className="font-bold underline">
                  privacy policy
                </Link>
              }/>
    </form>
    <PaymentErrorModal showPaymentErrorModal={showPaymentErrorModal} setShowPaymentErrorModal={setShowPaymentErrorModal} />
  </PageForCheckoutFlow>
}
